// =====================================================
// Button Group - Action Buttons
// =====================================================

import React from 'react';
import { Button } from '../types';
import { useChatStore } from '../store/chatStore';
import { sendMessage } from '../services/api';

interface ButtonGroupProps {
  buttons: Button[];
}

const ButtonGroup: React.FC<ButtonGroupProps> = ({ buttons }) => {
  const { userId, language, currentPage, addMessage, setIsLoading } = useChatStore();

  const handleButtonClick = async (button: Button) => {
  // Get the latest sessionId at click time to avoid stale closure issue
  // This fixes the race condition where sessionId might be null during initial render
  const currentSessionId = useChatStore.getState().sessionId;

  // Add user message
  addMessage({
    id: `msg_${Date.now()}`,
    sender: 'user',
    text: button.label,
    timestamp: new Date()
  });

  setIsLoading(true);

  try {
    // Send button action as message (use label as fallback)
    const response = await sendMessage({
      session_id: currentSessionId,
      user_id: userId,
      message: button.action || button.label,
      language,
      current_url: currentPage
    });

      // Add bot response
      addMessage({
        id: `msg_${Date.now() + 1}`,
        sender: 'bot',
        text: response.message,
        timestamp: new Date(),
        buttons: response.buttons,
        card: response.card,
        cards: response.cards,  // Include cards array for carousel
        dropdown: response.dropdown,  // Include dropdown options
        showAppointmentForm: response.showAppointmentForm  // Include appointment form flag
      });
    } catch (error) {
      console.error('Error sending message:', error);
      addMessage({
        id: `msg_${Date.now() + 1}`,
        sender: 'bot',
        text: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Separate navigation buttons from primary buttons
  const isNavigationButton = (button: Button): boolean => {
    const navActions = ['back', 'main_menu'];
    const navLabels = ['back', 'main menu', 'atrás', 'menú principal'];

    return navActions.includes(button.action.toLowerCase()) ||
           navLabels.includes(button.label.toLowerCase());
  };

  const primaryButtons = buttons.filter(button => !isNavigationButton(button));
  const navigationButtons = buttons.filter(button => isNavigationButton(button));

  // Get icon for navigation button
  const getNavIcon = (button: Button) => {
    const action = button.action.toLowerCase();
    const label = button.label.toLowerCase();

    // Back button - left arrow
    if (action === 'back' || label === 'back' || label === 'atrás') {
      return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      );
    }

    // Main Menu - home icon
    if (action === 'main_menu' || label.includes('main menu') || label.includes('menú principal')) {
      return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      );
    }

    return null;
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      {/* Primary buttons */}
      {primaryButtons.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {primaryButtons.map((button, index) => (
            <button
              key={`${button.key}-${index}`}
              onClick={() => handleButtonClick(button)}
              className="btn-olive text-xs px-2.5 py-1.5"
            >
              {button.label}
            </button>
          ))}
        </div>
      )}

      {/* Navigation buttons - minimal with icons, same row */}
      {navigationButtons.length > 0 && (
        <div className="flex items-center gap-2">
          {navigationButtons.map((button, index) => (
            <button
              key={`${button.key}-${index}`}
              onClick={() => handleButtonClick(button)}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs
                         text-betenbough-olive bg-betenbough-olive/10 hover:bg-betenbough-olive/20
                         rounded-full border border-betenbough-olive/30
                         transition-colors"
            >
              {getNavIcon(button)}
              <span>{button.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ButtonGroup;